import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserPlans } from 'src/app/dictionaries/UserPlans';
import { UserRoles } from 'src/app/dictionaries/UserRoles';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { environment } from 'src/environments/environment';

import { ClientMatterService } from 'src/app/services/client-matter.service';
import { DialogService } from 'src/app/services/dialog.service';
import { AuthService } from '../../services/auth.service';
import { FirebaseUtilitiesService } from '../../services/firebase-utilities.service';
import { UIMessagingService } from '../../services/uimessaging.service';
import { ClientProfileComponent } from '../clientprofile/clientprofile.component';
import { CreateFacilityUserComponent } from '../create-facility-user/create-facility-user.component';
import { CreateFacilityFormComponent } from '../create-facility-form/create-facility-form.component';
import { CreateclientComponent } from '../createclient/createclient.component';
import { CreatepatientComponent } from '../createpatient/createpatient.component';
import { CreateuserComponent } from '../createuser/createuser.component';
import { EditProfileComponent } from '../edit-profile/edit-profile.component';
import { ReportComponent } from '../report/report.component';
import { UpdateUserPasswordComponent } from '../update-user-password/update-user-password.component';
import { AuditLogViewerComponent } from './../../components/audit-log-viewer/audit-log-viewer.component';
import { Labels } from './../../dictionaries/labels';
import { RedirectionService } from './../../services/redirection.service';
import { SessionService } from './../../services/session.service';
import { TwoFactorAuthenticationService } from './../../services/two-factor-authentication.service';
import { AllusersComponent } from './../allusers/allusers.component';
import { SimpleMessageWindowComponent } from './../ui/simple-message-window/simple-message-window.component';
import { UpgradePlanComponent } from './../upgrade-plan/upgrade-plan.component';
import { JoinNuageDxNetworkComponent } from './../ui/join-nuagedx-network/join-nuagedx-network.component';
import { FacilitySetupComponent } from '../facility-setup/facility-setup.component';

const {
  clientMatterLabel,
  myProfileLabel,
  attorneysLabel,
  reportLabel,
  ownerLabel,
  adminLabel,
  clientUsersLabel,
  ownerListLabel,
  libraryLabel,
  sourcesLabel,
  libraryCategoriesLabel,
  consultantListLabel,
  facilityListLabel,
  associateListLabel,
  createClientMatterLabel,
  createUserLabel,
  videoConferencingLabel,
  upgradeYourPlanLabel,
  joinNuageDxNetworkLabel,
  auditLogLabel,
  logoutLabel
} = Labels;

const clientRole = 'client';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  subItemsVisible: any;
  userData: any;
  userPermissions: any;
  createuser = false;
  maintenance: boolean;
  public userEnabled = false;
  UserRoles: { owner: string; admin: string; associate: string; consultant: string; superuser: string; client: string };
  loaded: boolean;
  currentUrl: any;
  noUserData = true;
  activateMenuItems = true;
  ownerNeeded = false;
  owners: { value: string; label: string }[];
  facilityName: string = '';
  planLabel: 'Hub' | 'Spoke' | null = null;
  planText = {
    free: 'Free',
    fp: 'Free',
    goldyearly: 'Gold Annual',
    gold: 'Gold Monthly',
    silveryearly: 'Silver Annual',
    silver: 'Silver Monthly',
    lpmp: 'Clio Platinum',
    lpmyearly: 'Clio Platinum Annual',
    platinum: 'Platinum Monthly',
    platinumyearly: 'Platinum Annual'
  };

  auditLogLabel = auditLogLabel;
  logoutLabel = logoutLabel;
  upgradeYourPlanLabel = upgradeYourPlanLabel;
  joinNuageDxNetworkLabel = joinNuageDxNetworkLabel;
  videoConferencingLabel = videoConferencingLabel;
  createUserLabel = createUserLabel;
  createClientMatterLabel = createClientMatterLabel;
  associateListLabel = associateListLabel;
  consultantListLabel = consultantListLabel;
  facilityListLabel = facilityListLabel;
  ownerListLabel = ownerListLabel;
  libraryLabel = libraryLabel;
  sourcesLabel = sourcesLabel;
  libraryCategoriesLabel = libraryCategoriesLabel;
  clientUsersLabel = clientUsersLabel;
  adminLabel = adminLabel;
  ownerLabel = ownerLabel;
  reportLabel = reportLabel;
  attorneysLabel = attorneysLabel;
  myProfileLabel = myProfileLabel;
  clientMatterLabel = clientMatterLabel;

  @ViewChild('drawer', { static: true }) drawer: MatDrawer;

  private destroy$ = new Subject<void>();
  private unsubscribeAuth: (() => void) | null = null;

  @HostListener('document:click', ['$event'])
  clickout(event) {
    this.handleSessionTimeOut();
  }
  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.handleSessionTimeOut();
  }

  constructor(
    private dialog_$: DialogService,
    public router: Router,
    private clientMatter_$: ClientMatterService,
    public dialog: MatDialog,
    public auth_$: AuthService,
    private session_$: SessionService,
    private sessionStorage_$: SessionStorageService,
    private ngZone: NgZone,
    public redirection_$: RedirectionService,
    private twoFactorAuthentication_$: TwoFactorAuthenticationService,
    private uiMessaging_$: UIMessagingService,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private cdr: ChangeDetectorRef
  ) {
    this.userData = { userDBRole: '' };
    this.UserRoles = {
      owner: UserRoles.owner,
      admin: UserRoles.admin,
      associate: UserRoles.associate,
      client: UserRoles.client,
      consultant: UserRoles.consultant,
      superuser: UserRoles.superuser
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }

  getSessionTimeOutState() {
    const sessionTimeOutState = sessionStorage.getItem('sessionTimeOutState') || 'true';
    return sessionTimeOutState === 'true';
  }

  handleSessionTimeOut() {
    const sessionTimeOutState = this.getSessionTimeOutState();
    if (!sessionTimeOutState) return false;
    clearTimeout(this.session_$.sessionTimeout);
    if (this.auth_$.uid) this.setSessionTimeOut();
  }

  setSessionTimeOut() {
    if (!this.getSessionTimeOutState()) {
      return false;
    }
    this.session_$.clearSessionTimeOutState();
    this.session_$.sessionTimeout = setTimeout(() => {
      this.session_$.handleTimeEnd(v => {
        switch (v) {
          case 'session_end':
            this.dialog.closeAll();
            this.session_$.clearSessionTimeOutState();
            this.session_$.clearSessionTimeout();
            this.auth_$.logout();
            break;
          case 'session_continue':
            this.dialog.closeAll();
            this.setSessionTimeOut();
            break;
          default:
            this.dialog.closeAll();
            this.setSessionTimeOut();
            break;
        }
      });
    }, environment.config.sessionTimeLimit);
  }

  validatePermission(permission: string): boolean {
    if (!this.userPermissions || this.userPermissions.length === 0) {
      return false;
    }
    return this.userPermissions
      .find(p => p.role === this.auth_$.userData.getValue()['role'] && p.object === permission)?.access ?? false;
  }

  syncMyData($event) {
    this.auth_$.syncMyData().then(() => {
      this.uiMessaging_$.toastMessage('Your data has been synced', 'OK');
    });
  }

  public goToLogin() {
    this.redirection_$.goToLogin();
  }

  private async getOwnersData(): Promise<{ value: string; label: string }[]> {
    const { owners } = <{ owners: string[] }>this.auth_$.userData.getValue();
    return Promise.all(
      owners.map(async owner => ({
        value: owner,
        label: await this.auth_$.getOwnerName(owner)
      }))
    );
  }

  ngOnInit(): void {
    this.auth_$.drawer = this.drawer;
    this.loaded = false;

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(ev => {
      if (ev instanceof NavigationEnd) {
        this.ngZone.run(() => {
          this.currentUrl = ev.url;
        });
        if (ev.urlAfterRedirects === '/logout') {
          this.auth_$.logout();
        }
      }
    });

    this.maintenance = environment.maintenance;

    this.unsubscribeAuth = this.auth_$.auth.onAuthStateChanged(user => {
      if (this.sessionStorage_$.getSSO() === '0') {
        return;
      }

      if (user && Object.keys(user).length > 0) {
        this.handleSessionTimeOut();

        this.auth_$.userReady(user, 'onAuthStateChanged').then(async () => {
          const currentUserData = this.auth_$.userData.getValue() as any;
          if (currentUserData?.userDBRole === UserRoles.consultant) {
            this.owners = await this.getOwnersData();

            if (this.owners.length === 1) {
              this.ownerNeeded = false;
            } else if (this.owners.length > 1) {
              this.ownerNeeded = true;
            } else {
              this.ownerNeeded = true;
              this.uiMessaging_$.toastMessage(
                `No owners found for this user, please contact support before retry.`,
                'ALERT'
              );
            }
          }

          this.twoFactorAuthentication_$.handleTwoFactorSignIn(user.email);
        });

        this.auth_$.searchComponent = true;
      } else {
        this.ngZone.run(() => {
          this.userEnabled = false;
          this.loaded = true;
        });
        this.userData = null;
      }
    });

    const observer = this.auth_$.userData.pipe(takeUntil(this.destroy$)).subscribe({
      next: async (userData: any) => {
        if (Object.keys(userData).length === 0) {
          this.ngZone.run(() => (this.noUserData = true));
          return;
        }

        this.userEnabled = true;
        this.loaded = true;
        this.userData = this.auth_$.userData.getValue();
        this.planLabel = this.getPlanLabel();
        this.checkPasswordExpiration();

        if (this.shouldDisplayFacilityName()) {
          const facilityId = userData.facility ||
            (Array.isArray(userData.facilities) && userData.facilities.length > 0 ? userData.facilities[0] : null);
          this.facilityName = await this.getFacilityName(facilityId);
        } else {
          this.facilityName = '';
        }
        this.cdr.detectChanges();

        if (!userData['permissions']) return;

        this.userPermissions = userData['permissions'];
        this.createuser = this.validatePermission('createuser');
        this.noUserData = false;

        if (userData['user']) {
          this.userEnabled = true;
          this.loaded = true;

          if (!this.auth_$.preventRedirections) {
            observer.unsubscribe();
          }
        }
      },
      error: err => console.error('Something went wrong: ' + err)
    });
  }

  async openAuditLog() {
    const logs = await this.getLogsByUID(this.userData.user.uid);
    this.dialog_$.open(AuditLogViewerComponent, {
      width: '950px',
      data: { user: this.userData, logs }
    });
  }

  private async getLogsByUID(uid: string) {
    this.auth_$.showLoader('Getting logs');
    const logs = await this.firebaseUtilities_$.getLogsByUID(uid);
    this.auth_$.hideLoader();
    return logs;
  }

  validatePermissions(permission: string) {
    return this.auth_$.validatePermission(permission);
  }

  checkPasswordExpiration() {
    const { id } = <any>this.auth_$.userData.getValue();
    if (!id) {
      console.log('No user id found');
      this.auth_$.logout();
      return;
    }

    if (this.sessionStorage_$.getSSO() === '1') {
      return;
    }

    this.auth_$.checkIfPasswordIsExpired(id).then(passwordExpired => {
      if (passwordExpired.data) {
        this.handleUpdateUserPasswordComponent();
      }
    }).catch(err => {
      const msg = err?.message || (err && (err as any).code) || '';
      if (msg === 'internal' || (err && (err as any).code === 'internal')) {
        console.warn('Password expiration check unavailable (often CORS/Cloud Run in dev). Continuing.');
      } else {
        console.error('Failed to check password expiration:', err);
      }
    });
  }

  navigateToAddSources() {
    this.router.navigate(['addsources']);
  }

  handleUpdateUserPasswordComponent() {
    this.dialog
      .open(UpdateUserPasswordComponent, { width: '400px', disableClose: true, data: { mode: 'expiry' } })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: passwordData => this.handlePasswordData(passwordData) });
  }

  async handlePasswordData(passwordData) {
    if (!passwordData) return;
    const { newPassword, oldPassword } = passwordData;
    try {
      await this.auth_$.reauthenticateWithEmailPassword(oldPassword);
      const result: any = await this.auth_$.updateUserPassword(newPassword);
      if (result?.error === 'PASSWORD_HISTORY') {
        this.uiMessaging_$.toastMessage('This password was recently used. Please choose a different one.', null);
        return;
      }
      this.dialog_$.open(SimpleMessageWindowComponent, {
        width: '300px',
        data: { message: 'Your password has been updated', title: 'Password updated' }
      });
    } catch (err) {
      if (err?.code === 'auth/wrong-password') {
        this.uiMessaging_$.toastMessage('Your current password is wrong, please try again', null);
        this.checkPasswordExpiration();
      }
    }
  }

  editProfile(event) {
    this.dialog_$.open(EditProfileComponent, {
      width: '600px',
      data: this.userData
    });
  }

  openAllUsersComponent(role: string) {
    this.dialog_$.open(AllusersComponent, {
      width: '600px',
      data: { role }
    });
  }

  navigateToConsultant() {
    this.router.navigate(['consultants']);
  }

  navigateToFacility() {
    this.router.navigate(['facilities']);
  }

  navigateToFacilities() {
    this.router.navigate(['facilities-list']);
  }

  openCreateFacilityForm() {
    this.dialog_$.open(CreateFacilityFormComponent, {
      width: '600px',
      disableClose: false
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        console.log('Facility created successfully');
      }
    });
  }

  navigateToOwner() {
    this.router.navigate(['owners']);
  }

  navigateToAssociate() {
    this.router.navigate(['associates']);
  }

  navigateToMedicalRecords() {
    this.router.navigate(['admins'], { queryParams: { facilityuser: true } });
  }

  navigateToConsultants() {
    this.router.navigate(['consultants'], { queryParams: { facilityuser: true } });
  }

  navigateToAdmin() {
    this.router.navigate(['admins']);
  }

  navigateToClientUsers() {
    this.router.navigate(['clients']);
  }

  navigateToClient(type: string) {
    this.router.navigate(['clientsmatters', type]);
  }

  navigateToFeedLibrary() {
    this.router.navigate(['feedlibrary']);
  }

  navigateToLibraryCategories() {
    this.router.navigate(['librarycategories']);
  }

  navigateToAttorneysLawFirms() {
    this.router.navigate(['attorneys_lawfirms']);
  }

  private async getCaseInfo() {
    const patientData = await this.clientMatter_$.getPatientData(this.userData.clientMatter);
    const { ownerID } = patientData;
    const ownerPlanCode = await this.auth_$.getOwnerPlanCode(ownerID);
    return { ...patientData, ownerPlanCode };
  }

  checkIsSpoke() {
    if (!this.userData || !this.auth_$.userData.getValue()) {
      return false;
    }
    const plancode = this.auth_$.userData.getValue()['plancode'] || this.userData.plancode;
    return plancode === UserPlans.spoke;
  }

  getPlanLabel(): 'Hub' | 'Spoke' | null {
    const plancode = this.userData?.plancode;
    if (!plancode) return null;
    if (plancode === UserPlans.spoke) return 'Spoke';
    if (plancode.includes('platinum') || plancode === UserPlans.hub) return 'Hub';
    return null;
  }

  isOwnerAndSpoke(): boolean {
    if (!this.userData) return false;
    return this.userData.userDBRole === UserRoles.owner && this.checkIsSpoke();
  }

  async openClient() {
    const data = {
      patient: await this.getCaseInfo(),
      user_id: this.userData.user.uid,
      targetFolder: ''
    };
    this.dialog_$.open(ClientProfileComponent, {
      data,
      width: '100vw',
      id: 'client-profile'
    });
  }

  navigateToCreateClient() {
    this.router.navigate(['createclientmatter']);
  }

  goToComingSoon(type?: string) {
    this.router.navigate(['comingsoon'], { queryParams: { type } });
  }

  upgradeYourPlan() {
    const userDataValue = this.auth_$.userData.getValue();
    const userPlan = userDataValue['plancode'] || 'fp';
    this.dialog_$.open(UpgradePlanComponent, {
      width: '90vw',
      maxWidth: '90vw',
      data: { title: 'Upgrade Plan', currentPlan: userPlan, message: '<p>Go and upgrade your current plan.</p>' }
    });
  }

  openJoinNuageDxNetwork() {
    this.dialog_$.open(JoinNuageDxNetworkComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {}
    });
  }

  openFacilitySetup(event: Event): void {
    event?.stopPropagation();
    this.drawer.close();
    const facilityId =
      this.userData?.facility ||
      (Array.isArray(this.userData?.facilities) && this.userData.facilities.length > 0
        ? this.userData.facilities[0]
        : null);
    const ref = this.dialog_$.open(FacilitySetupComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { facilityName: this.facilityName, facilityId, userData: this.userData }
    });
    ref.afterClosed().subscribe(result => {
      if (!result) return;
      const snack = this.uiMessaging_$.toastMessage(
        'Facility setup complete. Next: create your first user.',
        'Create Users',
        { duration: 12000 }
      );
      snack.onAction().subscribe(() => this.navigateToCreateAdminUser());
    });
  }

  VideoConferencing() {
    if (this.userEnabled) {
      this.router.navigate(['meetings']);
    }
    this.drawer.close();
  }

  async createUser(role: string) {
    if (!this.userEnabled) {
      this.drawer.close();
      return;
    }

    switch (role) {
      case 'consultant':
        this.dialog_$.open(CreateuserComponent, {
          width: '680px',
          data: { userType: 'Consultant', title: 'Create Consultant', button: 'Create', screen: 'full' }
        });
        break;
      case 'admin':
        this.dialog_$.open(CreateuserComponent, {
          width: '680px',
          data: {
            userType: 'Admin',
            title: 'Create a user',
            button: 'Create',
            screen: 'full',
            limits: { limits: await this.checkPlanLimits() }
          }
        });
        break;
      case clientRole:
        this.openCreatePatientDialog('500px');
        break;
      case UserRoles.owner:
        if (this.userData.userDBRole === UserRoles.superuser) {
          this.navigateToCreateOwner();
        } else {
          this.uiMessaging_$.toastMessage('You are not authorized to access this page', null);
        }
        break;
      default:
        break;
    }
  }

  navigateToCreateUser() {
    this.openCreatePatientDialog('550px');
  }

  private async openCreatePatientDialog(width: string) {
    try {
      const facilities = await this.firebaseUtilities_$.getFacilities();
      this.dialog_$.open(CreatepatientComponent, {
        width,
        data: {
          facilities: facilities.map(facility => ({
            facilityId: facility.id,
            facilityName: facility.name
          }))
        }
      });
    } catch (error) {
      console.error('Error fetching facilities:', error);
      this.dialog_$.open(CreatepatientComponent, { width, data: null });
    }
  }

  navigateToCreateOwner() {
    this.dialog_$.open(CreateclientComponent, {
      width: '680px',
      data: null
    });
  }

  navigateGenerateReport() {
    this.dialog_$.open(ReportComponent, {
      width: '70vw',
      data: this.userData?.user?.uid ?? null
    });
  }

  hasNotifications() {
    if (environment.config.notifications === 0) {
      return false;
    }
    return this.auth_$.hasNotifications();
  }

  isNotSilver() {
    return this.auth_$.isNotSilverPlan();
  }

  isOwnerAndNotSpoke(): boolean {
    const userData = this.auth_$.userData.getValue();
    const isOwner = userData['userDBRole'] === UserRoles.owner;
    const isSpoke = userData['plancode'] === UserPlans.spoke;
    return isOwner && !isSpoke;
  }

  canManageUsers(): boolean {
    if (!this.userData?.userDBRole) return false;
    return ![UserRoles.superuser, UserRoles.consultant, UserRoles.client].includes(this.userData.userDBRole);
  }

  canCreatePatient(): boolean {
    if (!this.userData?.userDBRole) return false;
    return [UserRoles.owner, UserRoles.associate, UserRoles.admin].includes(this.userData.userDBRole);
  }

  canCreateFacilityUser(): boolean {
    if (!this.userData?.userDBRole) return false;
    return ![UserRoles.superuser, UserRoles.consultant, UserRoles.admin, UserRoles.client].includes(this.userData.userDBRole);
  }

  canUseVideoConferencing(): boolean {
    if (!this.userData?.userDBRole) return false;
    return ![UserRoles.admin, UserRoles.consultant, UserRoles.superuser, UserRoles.client, UserRoles.associate].includes(this.userData.userDBRole);
  }

  isOwnerRole(): boolean {
    return this.userData?.userDBRole === UserRoles.owner;
  }

  isNotClientRole(): boolean {
    return this.userData?.userDBRole !== UserRoles.client;
  }

  async checkPlanLimits() {
    const [limits, users] = await Promise.all([
      this.auth_$.getPlanLimits(this.auth_$.userData.value['plancode']),
      this.auth_$.getUsersCount()
    ]);
    return { limits, users };
  }

  navigateToCreateClientMatter() {
    this.drawer.close();
    this.router.navigate(['createclientsmatters']);
    this.openCreatePatientDialog('550px');
  }

  navigateToCreateAdminUser() {
    this.dialog_$.open(CreateFacilityUserComponent, {
      data: {
        title: 'Create Facility User',
        role: this.userData?.userDBRole,
        limits: null
      }
    });
  }

  toggleSubItems($event: MouseEvent) {
    this.subItemsVisible = !this.subItemsVisible;
  }

  shouldDisplayFacilityName(): boolean {
    if (!this.userData) return false;

    const role = this.userData.userDBRole;
    const hasFacility = !!this.userData.facility;
    const hasFacilities = Array.isArray(this.userData.facilities) && this.userData.facilities.length > 0;

    if (role === UserRoles.admin) return hasFacility && !!this.userData.facilityuser;
    if (role === UserRoles.consultant) return hasFacility;
    if (role === UserRoles.owner) return hasFacility || hasFacilities;
    return false;
  }

  private async getFacilityName(facilityId: string): Promise<string> {
    if (!facilityId) return '';
    try {
      const facilities = await this.firebaseUtilities_$.getFacilities();
      const facility = facilities.find(f => f.id === facilityId || f.name?.toLowerCase() === facilityId.toLowerCase());
      return facility?.name || facilityId;
    } catch (error) {
      console.error('Error fetching facility name:', error);
      return facilityId;
    }
  }
}
